import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useTheme } from "./ThemeContext";
import { setStorageNamespace } from "../lib/openPlayStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = auth, false = guest
  const { setTheme } = useTheme();

  const applyUserPrefs = useCallback((u) => {
    if (u?.user_id) setStorageNamespace(u.user_id);
    if (u?.settings?.theme) setTheme(u.settings.theme);
  }, [setTheme]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyUserPrefs(data);
    } catch (e) {
      setUser(false);
    }
  }, [applyUserPrefs]);

  useEffect(() => {
    // Skip /me during OAuth callback; AuthCallback establishes session first.
    if (window.location.hash?.includes("session_id=")) return;
    // Public spectator route needs no auth.
    if (window.location.pathname.startsWith("/live/")) { setUser(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    applyUserPrefs(data);
    return data;
  };

  const register = async (email, name, password, accountType, profile) => {
    const { data } = await api.post("/auth/register", { email, name, password, account_type: accountType, profile });
    setUser(data);
    applyUserPrefs(data);
    return data;
  };

  const requestRegisterCode = async (email, name, password, accountType, profile, firstName, lastName) => {
    await api.post("/auth/register/request-code", { email, name, password, account_type: accountType, profile, first_name: firstName, last_name: lastName });
  };

  const verifyRegister = async (email, code) => {
    const { data } = await api.post("/auth/register/verify", { email, code });
    setUser(data);
    applyUserPrefs(data);
    return data;
  };

  const completeProfile = async (accountType, profile) => {
    const { data } = await api.post("/auth/complete-profile", { account_type: accountType, profile });
    setUser(data);
    applyUserPrefs(data);
    return data;
  };

  const updateProfile = async (patch) => {
    const { data } = await api.put("/auth/profile", patch);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(false);
  };

  const updateSettings = async (patch) => {
    const { data } = await api.put("/auth/settings", patch);
    setUser((u) => (u ? { ...u, settings: data } : u));
    if (patch.theme) setTheme(patch.theme);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, requestRegisterCode, verifyRegister, completeProfile, updateProfile, logout, updateSettings, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
